import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SearchDropdown from "./SearchDropdown";
import { useAppContext } from "../../context/AppContext";
import { API_BASE_URL } from "../../config";
import CouponBanner from "./CouponBanner";

const normalizeAvatar = (path) => {
  if (!path) return "/assets/img/a1-img/avatar-placeholder.png";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const cleanPath = String(path).replace(/\\/g, "/").replace(/^\/+/, "");
  return `${API_BASE_URL}/${cleanPath}`;
};

export default function Navbar() {
  const {
    user,
    cartItems = [],
    logout,
    updateCartQuantity,
    removeCartItem,
  } = useAppContext();

  const [showDropdown, setShowDropdown] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const [isDesktopSearch, setIsDesktopSearch] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1200 : true,
  );
  const [isSticky, setIsSticky] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const dropdownRef = useRef(null);
  const desktopSearchContainerRef = useRef(null);
  const mobileSearchContainerRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const cartSubtotal = useMemo(() => {
    return cartItems.reduce(
      (total, item) =>
        total + Number(item.price || 0) * Number(item.quantity || 0),
      0,
    );
  }, [cartItems]);

  const closeOverlays = () => {
    if (typeof window !== "undefined" && window.bootstrap) {
      const activeModals = document.querySelectorAll(".modal.show");
      const activeOffcanvas = document.querySelectorAll(".offcanvas.show");

      activeModals.forEach((modal) => {
        window.bootstrap.Modal.getOrCreateInstance(modal).hide();
      });

      activeOffcanvas.forEach((oc) => {
        window.bootstrap.Offcanvas.getOrCreateInstance(oc).hide();
      });
    }

    setDesktopSearchOpen(false);
    setMobileSearchOpen(false);
    setShowDropdown(false);
  };

  useEffect(() => {
    closeOverlays();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let prevIsDesktop = window.innerWidth >= 1200;
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1200;
      // Only reset search states if we actually transition between mobile and desktop modes
      if (isDesktop !== prevIsDesktop) {
        setIsDesktopSearch(isDesktop);
        setDesktopSearchOpen(false);
        setMobileSearchOpen(false);
        prevIsDesktop = isDesktop;
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;

      // Profile dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowDropdown(false);
      }

      // Only check active search container. 
      // On mobile, the dropdown is fixed and handles its own events, but we keep the row ref check as fallback.
      if (isDesktopSearch) {
        if (
          desktopSearchContainerRef.current &&
          !desktopSearchContainerRef.current.contains(target)
        ) {
          setDesktopSearchOpen(false);
        }
      } else if (mobileSearchOpen) {
        if (
          mobileSearchContainerRef.current &&
          !mobileSearchContainerRef.current.contains(target)
        ) {
          setMobileSearchOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktopSearch, mobileSearchOpen]);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    closeOverlays();
    navigate("/", { replace: true, state: { logoutSuccess: true } });
  };
  useEffect(() => {
    if (location.state?.logoutSuccess) {
      setToast({
        show: true,
        type: "success",
        message: "Logged out successfully",
      });

      window.history.replaceState({}, document.title);

      const timer = setTimeout(() => {
        setToast({ show: false, type: "success", message: "" });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Reset image error state when user changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.id, user?.avatar]);

  const handleImgError = (e) => {
    setAvatarError(true);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown((prev) => !prev);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleCartQuantityChange = (itemIndex, delta) => {
    updateCartQuantity(itemIndex, delta);
  };

  const handleRemoveFromCart = (e, itemIndex) => {
    e.preventDefault();
    removeCartItem(itemIndex);
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/categories", label: "Shop Now" },
    { to: "/combo_offer", label: "Combos & Offers" },
    { to: "/aboutus", label: "About Us" },
    // { to: "/franchise", label: "Franchise" },
    { to: "/contact-us", label: "Contact Us" },
  ];

  const renderSearchBox = (mode = "desktop") => {
    const isMobile = mode === "mobile";

    const isOpen = isMobile ? mobileSearchOpen : desktopSearchOpen;

    const openSearch = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (isMobile) {
        setMobileSearchOpen(true);
        setDesktopSearchOpen(false);
      } else {
        setDesktopSearchOpen(true);
        setMobileSearchOpen(false);
      }
    };

    return (
      <div
        className="bd-action__filter p-relative"
        style={{
          position: "relative",
          width: "100%",
          zIndex: 9999,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <form
          onSubmit={(e) => e.preventDefault()}
          style={{
            position: "relative",
            zIndex: 2,
          }}
        >
          <input
            type="text"
            placeholder="Search products..."
            readOnly
            onClick={openSearch}
          />

          <button className="search-btn" type="button" onClick={openSearch}>
            <i className="flaticon-magnifiying-glass"></i>
          </button>
        </form>

        <div
          className={`navbar-search-dropdown ${isOpen ? "active" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <SearchDropdown
            isOpen={isOpen}
            onClose={() => {
              if (isMobile) {
                setMobileSearchOpen(false);
              } else {
                setDesktopSearchOpen(false);
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="offcanvas offcanvas-end"
        id="cartmini"
        tabIndex="-1"
        aria-labelledby="cartminiLabel"
      >
        <div className="cartmini__wrapper">
          <div className="cartmini__title">
            <h4 id="cartminiLabel">Shopping Cart</h4>
          </div>

          <div className="cartmini__close">
            <button
              type="button"
              className="cartmini__close-btn"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
            >
              <i className="fal fa-times"></i>
            </button>
          </div>

          <div className="cartmini__widget">
            <div className="cartmini__inner">
              <ul>
                {cartItems.length === 0 ? (
                  <li className="p-3 text-center list-unstyled">
                    Your cart is empty.
                  </li>
                ) : (
                  cartItems.map((item, index) => (
                    <li key={item._id || item.id || index}>
                      <div className="cartmini__thumb">
                        <a href="#!" onClick={(e) => e.preventDefault()}>
                          <img src={item.img} alt={item.name} />
                        </a>
                      </div>

                      <div className="cartmini__content">
                        <h5>
                          <a href="#!" onClick={(e) => e.preventDefault()}>
                            {item.name}
                          </a>
                        </h5>

                        <span className="d-block small text-muted mb-2">
                          Weight: {item.net_wt || "N/A"}
                        </span>

                        <div className="product-quantity mt-2 mb-2">
                          <span
                            className="cart-minus"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleCartQuantityChange(index, -1)}
                            onKeyDown={(e) =>
                              (e.key === "Enter" || e.key === " ") &&
                              handleCartQuantityChange(index, -1)
                            }
                          >
                            -
                          </span>

                          <input
                            className="cart-input"
                            type="text"
                            value={item.quantity}
                            readOnly
                          />

                          <span
                            className="cart-plus"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleCartQuantityChange(index, 1)}
                            onKeyDown={(e) =>
                              (e.key === "Enter" || e.key === " ") &&
                              handleCartQuantityChange(index, 1)
                            }
                          >
                            +
                          </span>
                        </div>

                        <div className="product__sm-price-wrapper">
                          <span className="product__sm-price">
                            Rs.{" "}
                            {(
                              Number(item.price || 0) *
                              Number(item.quantity || 0)
                            ).toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <a
                        href="#!"
                        className="cartmini__del"
                        onClick={(e) => handleRemoveFromCart(e, index)}
                      >
                        <i className="fal fa-times"></i>
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </div>

            <div className="cartmini__checkout">
              <div className="cartmini__checkout-title mb-30">
                <h4>Subtotal:</h4>
                <span>Rs. {cartSubtotal.toFixed(2)}</span>
              </div>

              <div className="cartmini__checkout-btn d-flex flex-nowrap">
                <Link
                  to="/cart"
                  className="bd-fill__btn me-2"
                  onClick={closeOverlays}
                >
                  <span></span> view cart
                </Link>
                <Link
                  to="/checkout"
                  className="bd-unfill__btn my-3"
                  onClick={closeOverlays}
                >
                  <span></span> checkout
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="offcanvas__area">
        <div
          className="offcanvas offcanvas-start"
          id="offcanvasmodal"
          tabIndex="-1"
          aria-labelledby="offcanvasmodalLabel"
          style={{ width: "100%", maxWidth: "100%" }}
        >
          <div
            className="offcanvas__wrapper"
            style={{
              height: "100vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="offcanvas__close d-flex justify-content-end">
              <button
                type="button"
                className="offcanvas__close-btn"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
              >
                <i className="fal fa-times"></i>
              </button>
            </div>

            <div className="offcanvas__content p-4 flex-grow-1 d-flex flex-column">
              <div className="mobile-menu fix mb-40">
                <nav>
                  <ul className="list-unstyled">
                    {navLinks.map((item) => (
                      <li key={item.to} className="mb-4">
                        <Link
                          to={item.to}
                          onClick={closeOverlays}
                          className="text-dark fs-5"
                          style={{ textDecoration: "none" }}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>

            <div className="offcanvas__auth p-4 border-top">
              <ul className="list-unstyled">
                {user ? (
                  <>
                    <li className="mb-3">
                      <Link to="/profile" onClick={closeOverlays}>
                        <i className="fal fa-user me-2"></i> My Profile
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="text-danger border-0 bg-transparent p-0"
                      >
                        <i className="fal fa-sign-out-alt me-2"></i> Logout
                      </button>
                    </li>
                  </>
                ) : (
                  <li>
                    <Link
                      to="/login"
                      onClick={closeOverlays}
                      className="text-dark"
                    >
                      <i className="fal fa-sign-in me-2"></i> Login / Register
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            <div className="social-icons">
              <ul className="d-flex justify-content-center justify-content-xl-start gap-3 list-unstyled">
                <li>
                  <a href="https://www.facebook.com/a1sweeetsmadurai/">
                    <img
                      src="/assets/img/a1-img/social_icons/facebook.png"
                      alt="Facebook"
                      style={{ width: "30px", height: "30px" }}
                    />
                  </a>
                </li>
                <li>
                  <a href="https://www.youtube.com/@a1sweetbakery500">
                    <img
                      src="/assets/img/a1-img/social_icons/youtube.png"
                      alt="YouTube"
                      style={{ width: "30px", height: "30px" }}
                    />
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/a1bakeryandsweets/">
                    <img
                      src="/assets/img/a1-img/social_icons/insta.png"
                      alt="Instagram"
                      style={{ width: "30px", height: "30px" }}
                    />
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/919751336638">
                    <img
                      src="/assets/img/a1-img/social_icons/whatsapp.png"
                      alt="WhatsApp"
                      style={{ width: "30px", height: "30px" }}
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="added-to-cart">
        <div className="added-to-cart-content d-flex align-items-center">
          <i className="fa-light fa-check"></i>
          <p>Successfully Added to cart</p>
        </div>
      </div>

      <header>
        {location.pathname !== "/checkout" &&
          !location.pathname.startsWith("/order-success") && (
            <CouponBanner />
          )}
        <div className="bd-header__border">
          <div id="header-sticky" className="bd-header__bottom-area-3">
            <div className="container">
              <div className="row align-items-center">
                <div className="col-4 d-xl-none">
                  <div className="header__hamburger">
                    <button
                      type="button" // Hamburger menu button
                      data-bs-toggle="offcanvas"
                      data-bs-target="#offcanvasmodal"
                      className="hamburger-btn"
                    >
                      <span className="hamburger-icon bg-white">
                        <span></span>
                        <span></span>
                        <span></span>
                      </span>
                    </button>
                  </div>
                </div>

                <div className="col-xxl-2 col-xl-2 col-lg-4 col-md-4 col-4">
                  <div className="bd-header__logo-3 text-center text-lg-start">
                    <Link to="/">
                      <img src="/assets/img/a1-img/Logo.png" alt="logo" />
                    </Link>
                  </div>
                </div>

                <div className="col-xxl-7 col-xl-6 d-none d-xl-block">
                  <div className="bd-header__left-3 d-flex justify-content-center align-items-center w-100">
                    <div className="main-menu d-none d-xl-block">
                      <nav id="mobile-menu">
                        <ul className="d-flex justify-content-center align-items-center mb-0 list-unstyled" style={{ gap: "32px" }}>
                          {navLinks.map((item) => (
                            <li key={item.to} className="m-0">
                              <Link
                                to={item.to}
                                className={location.pathname === item.to ? "active" : ""}
                              >
                                {item.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>

                <div className="col-xxl-3 col-xl-4 col-lg-4 col-md-4 col-4">
                  <div className="bd-header__right header__right-3 d-flex align-items-center justify-content-end">
                    {isDesktopSearch && (
                      <div
                        ref={desktopSearchContainerRef}
                        className="bd-action__filter-wrapper d-none d-xl-block"
                        style={{
                          position: "relative",
                          zIndex: 4000,
                          width: "100%",
                          maxWidth: "260px",
                        }}
                      >
                        {renderSearchBox("desktop")}
                      </div>
                    )}

                    <div className="bd-action__cart-list list-3 d-flex justify-content-end align-items-center">
                      <div className="bd-action__item">
                        <div className="bd-action__cart-wrapper">
                          <div className="bd-action__cart-icon"> {/* Cart icon */}
                            <a
                              href="#!"
                              data-bs-toggle="offcanvas"
                              data-bs-target="#cartmini"
                              aria-controls="cartmini"
                              onClick={(e) => e.preventDefault()}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16.508"
                                height="18.5"
                                viewBox="0 0 16.508 18.5"
                              >
                                <path
                                  d="M24.21,35.5H34.3a3.214,3.214,0,0,0,3.21-3.21v-9.6a.571.571,0,0,0-.569-.569H33.523v-.854a4.269,4.269,0,0,0-8.538,0v.854H21.569a.571.571,0,0,0-.569.569v9.6A3.214,3.214,0,0,0,24.21,35.5Zm1.913-14.231a3.131,3.131,0,0,1,6.262,0v.854H26.123Zm-3.985,1.992h2.846v1.423a.569.569,0,0,0,1.138,0V23.262h6.262v1.423a.569.569,0,0,0,1.138,0V23.262h2.846V32.29A2.076,2.076,0,0,1,34.3,34.362H24.21a2.076,2.076,0,0,1-2.072-2.072Z"
                                  transform="translate(-21 -17)"
                                  fill="#ffffff"
                                />
                              </svg>
                            </a>
                            <span className="bd-action__item-number cart-count">
                              {cartItems.length}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bd-action__item d-none d-xl-flex align-items-center">
                        {user ? (
                          <div // User profile dropdown
                            className="bd-action__cart dropdown"
                            ref={dropdownRef}
                          >
                            <div
                              className="bd-action__cart-icon"
                              onClick={toggleDropdown}
                              style={{ cursor: "pointer" }}
                            >
                              {(user.avatar || user.img) && !avatarError ? (
                                <img
                                  src={normalizeAvatar(user.avatar || user.img)}
                                  onError={handleImgError}
                                  alt="Profile"
                                  className="rounded-circle"
                                  width="40"
                                  height="40"
                                  style={{
                                    border: "2px solid #fff",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center bg-danger text-white fw-bold"
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    fontSize: "12px",
                                    border: "2px solid #fff",
                                  }}
                                >
                                  {getInitials(user.name)}
                                </div>
                              )}
                            </div>

                            {showDropdown && (
                              <ul
                                className="dropdown-menu show"
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  right: 0,
                                  zIndex: 1050,
                                  minWidth: "180px",
                                  background: "#fff",
                                  borderRadius: "8px",
                                  boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                  border: "1px solid #e9ecef",
                                  padding: 0,
                                  margin: 0,
                                  listStyle: "none",
                                }}
                              >
                                <li>
                                  <Link
                                    className="dropdown-item px-3 py-2 d-flex align-items-center text-dark bg-white"
                                    to="/profile"
                                    onClick={() => setShowDropdown(false)}
                                    style={{
                                      textDecoration: "none",
                                      fontWeight: "500",
                                    }}
                                  >
                                    <i className="fal fa-user me-3 fs-6"></i>
                                    My Profile
                                  </Link>
                                </li>
                                <li>
                                  <hr className="dropdown-divider my-0" />
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item px-3 py-2 w-100 text-start border-0 bg-transparent text-danger"
                                    onClick={handleLogout}
                                    style={{
                                      fontWeight: "500",
                                      fontSize: "14px",
                                    }}
                                  >
                                    <i className="fal fa-sign-out-alt me-3 fs-6"></i>
                                    Logout
                                  </button>
                                </li>
                              </ul>
                            )}
                          </div>
                        ) : (
                          <div className="bd-action__cart">
                            <div className="bd-action__cart-icon">
                              <Link to="/login">
                                <i
                                  className="fal fa-user"
                                  style={{
                                    color: "#ffffff",
                                    fontSize: "20px",
                                    lineHeight: "1",
                                  }}
                                />
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Search Row - visible below logo for mobile/tablet when not sticky */}
              {!isDesktopSearch && !isSticky && (
                <div
                  className="row d-xl-none pb-3 px-3 mobile-search-row"
                  ref={mobileSearchContainerRef}
                >
                  <div className="col-12">{renderSearchBox("mobile")}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {toast.show && (
        <div
          className={`position-fixed top-0 end-0 mt-5 me-3 p-3 text-white rounded shadow-lg ${toast.type === "success" ? "bg-success" : "bg-danger"
            }`}
          style={{ zIndex: 11000 }}
        >
          {toast.message}
        </div>
      )}
    </>
  );
}
